import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowUpRight, ImagePlus, Play, RefreshCw, RotateCcw, Sparkles, X } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../../api/client";
import type { Modality, PlaygroundCapability, PlaygroundGeneration, PlaygroundModelCapability, PlaygroundTestMode } from "../../api/types";
import { useGatewayPermissions } from "../../auth/permissions";
import { formatMoney, StatusBadge } from "../../components/ui";
import type { LightboxRequest } from "./PlaygroundLightbox";
import {
  playgroundRoutingLabel, aspectRatioLabel, buildPlaygroundPayload, clientRequestIdFor, durationLabel, elapsedSeconds, formatElapsed,
  generationMedia, isAmbiguousSubmitFailure, isTerminalStatus, newClientRequestId, parseGenerationStatus,
  payloadFingerprint, playgroundModels, PLAYGROUND_POLL_INTERVAL_MS, PLAYGROUND_UPLOAD_TYPES, resolutionLabel,
  resolvePlaygroundForm, sameSelection, validateUploadFile, withClientRequestId,
  type PendingSubmit, type PlaygroundMedia, type PlaygroundPayload, type PlaygroundSelection,
} from "./playgroundLogic";

interface UploadItem {
  id: string;
  file: File;
  previewUrl: string;
  status: "uploading" | "done" | "error";
  url: string;
  error: string;
}

const panelCopy: Record<Modality, { title: string; placeholder: string; resolution: string; submit: string; uploadHelp: string }> = {
  image: {
    title: "图片生成",
    placeholder: "描述想生成的画面，例如：雪地里奔跑的红狐狸，电影感侧逆光",
    resolution: "画质",
    submit: "生成图片",
    uploadHelp: "不传图为文生图，上传后为图生图。",
  },
  video: {
    title: "视频生成",
    placeholder: "描述画面与镜头运动，例如：镜头缓慢推进，海浪拍打礁石",
    resolution: "分辨率",
    submit: "生成视频",
    uploadHelp: "0 张文生视频；1 张优先首帧图生视频；2 张优先参考图生视频，否则按首尾帧；3 张及以上为参考图生视频。",
  },
};

const emptySelection: PlaygroundSelection = { model: "", aspectRatio: "", resolution: "", durationSeconds: 0 };

// PlaygroundGenerationPanel owns one modality's form, uploads, submit, polling and result.
// 单个模态的表单、参考图上传、提交、轮询和结果展示。
export function PlaygroundGenerationPanel({ modality, hidden, capabilities, testModels, routingSupported, onPreview }: {
  modality: Modality;
  hidden: boolean;
  capabilities: PlaygroundCapability[];
  testModels: PlaygroundModelCapability[];
  routingSupported: boolean;
  onPreview: (request: LightboxRequest) => void;
}) {
  const fieldId = useId();
  const queryClient = useQueryClient();
  const copy = panelCopy[modality];
  const submitLock = useRef(false);
  const uploadsRef = useRef<UploadItem[]>([]);
  const [testMode, setTestMode] = useState<PlaygroundTestMode>("model");
  const [channelId, setChannelId] = useState(0);
  const [draft, setDraft] = useState<PlaygroundSelection>(emptySelection);
  const [prompt, setPrompt] = useState("");
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [uploadNotice, setUploadNotice] = useState("");
  const [pending, setPending] = useState<PendingSubmit | null>(null);
  const [submitError, setSubmitError] = useState("");
  const [requestId, setRequestId] = useState("");
  const [deduplicated, setDeduplicated] = useState(false);

  const catalog = (testMode === "policy" ? capabilities : testModels).filter((item) => item.modality === modality);
  const selectedModel = catalog.find((item) => item.model === draft.model) || catalog[0];
  const channels = testModels.find((item) => item.model === selectedModel?.model)?.channels || [];
  const selectedChannel = channels.find((item) => item.channel_id === channelId);
  const models = useMemo(() => catalog.map((item) => {
    const source = testMode === "channel" && item.model === selectedModel?.model ? selectedChannel : item;
    return (source ? playgroundModels([source], modality)[0] : null) || { key: item.model, modality, routes: [] };
  }), [catalog, modality, selectedChannel, selectedModel?.model, testMode]);
  const form = useMemo(() => resolvePlaygroundForm(models, modality, draft, uploads.length), [draft, modality, models, uploads.length]);
  uploadsRef.current = uploads;

  // Persist the clamped selection so a later model or image-count change starts from what the user sees.
  // 把收敛后的选择写回草稿，后续切换模型或图片数量时以界面所见为准。
  useEffect(() => {
    if (!sameSelection(draft, form.selection)) setDraft(form.selection);
  }, [draft, form.selection]);
  useEffect(() => () => uploadsRef.current.forEach((item) => revokePreview(item.previewUrl)), []);

  const startUpload = (item: UploadItem) => {
    api.playgroundUpload(item.file).then(
      (response) => setUploads((current) => current.map((entry) => entry.id === item.id ? { ...entry, status: "done", url: response.data.url, error: "" } : entry)),
      (error: unknown) => setUploads((current) => current.map((entry) => entry.id === item.id ? { ...entry, status: "error", error: errorMessage(error) } : entry)),
    );
  };
  const addFiles = (files: File[]) => {
    if (!files.length) return;
    const room = Math.max(form.uploadLimit - uploads.length, 0);
    const notices: string[] = [];
    const accepted: UploadItem[] = [];
    files.forEach((file) => {
      const invalid = validateUploadFile(file);
      if (invalid) notices.push(invalid);
      else if (accepted.length >= room) notices.push(`${file.name}：已达到 ${form.uploadLimit} 张上限`);
      else accepted.push({ id: newClientRequestId(), file, previewUrl: createPreview(file), status: "uploading", url: "", error: "" });
    });
    setUploadNotice(notices.join("；"));
    if (!accepted.length) return;
    setUploads((current) => [...current, ...accepted]);
    accepted.forEach(startUpload);
  };
  const removeUpload = (id: string) => {
    const target = uploads.find((item) => item.id === id);
    if (target) revokePreview(target.previewUrl);
    setUploads((current) => current.filter((item) => item.id !== id));
    setUploadNotice("");
  };
  const retryUpload = (item: UploadItem) => {
    setUploads((current) => current.map((entry) => entry.id === item.id ? { ...entry, status: "uploading", error: "" } : entry));
    startUpload(item);
  };

  const submit = useMutation({
    // Paid work is never retried automatically; only an explicit click resubmits.
    // 付费提交绝不自动重试，只有用户明确点击才会再次提交。
    retry: false,
    mutationFn: (payload: PlaygroundPayload) => payload.modality === "image" ? api.playgroundSubmitImage(payload.input) : api.playgroundSubmitVideo(payload.input),
    onSuccess: (response) => {
      setPending(null);
      setSubmitError("");
      setDeduplicated(Boolean(response.deduplicated));
      queryClient.setQueryData(["playground-generation", response.data.request_id], { data: response.data });
      setRequestId(response.data.request_id);
      void queryClient.invalidateQueries({ queryKey: ["playground-history"] });
    },
    onError: (error) => {
      // An ambiguous failure may already be accepted server-side; keep the id so a retry is deduplicated, not paid twice.
      // 不确定是否已受理的失败保留请求 ID，重试会命中幂等而不是再付费一次。
      if (!isAmbiguousSubmitFailure(error)) setPending(null);
      setSubmitError(errorMessage(error));
    },
    onSettled: () => { submitLock.current = false; },
  });

  const uploading = uploads.some((item) => item.status === "uploading");
  const uploadFailed = uploads.some((item) => item.status === "error");
  const referenceUrls = uploads.filter((item) => item.status === "done").map((item) => item.url);
  const routeBlocked = !routingSupported ? "测试服务需要升级，当前无法保证模型和渠道锁定，请刷新或联系管理员"
    : testMode === "channel" && !selectedChannel ? "请选择要测试的渠道"
    : testMode === "channel" && selectedChannel?.unavailable_reason ? selectedChannel.unavailable_reason
    : !form.model?.routes.length ? "所选模型没有测试 Consumer 已授权的可用直接路由，请选择其他模型或业务策略测试" : "";
  const draftPayload = uploading || uploadFailed || routeBlocked ? null : buildPlaygroundPayload(form, prompt, referenceUrls, "", {
    test_mode: testMode, ...(testMode === "channel" ? { channel_id: channelId } : {}),
  });
  const disabledReason = routeBlocked || form.blockedReason
    || (uploading ? "参考图上传中，请稍候" : "")
    || (uploadFailed ? "有参考图上传失败，请重试或删除" : "")
    || (!prompt.trim() ? "请输入提示词" : "");
  const canSubmit = Boolean(draftPayload) && !submit.isPending;
  const retrySameId = Boolean(pending && draftPayload && pending.fingerprint === payloadFingerprint(draftPayload));

  // startSubmit mints a new client_request_id per click; only an unchanged retry after an ambiguous failure reuses it.
  // 每次点击生成新的 client_request_id；只有不确定失败后原样重试才复用。
  const startSubmit = () => {
    if (!draftPayload || submitLock.current) return;
    const fingerprint = payloadFingerprint(draftPayload);
    const clientRequestId = clientRequestIdFor(pending, fingerprint, newClientRequestId);
    submitLock.current = true;
    setPending({ clientRequestId, fingerprint });
    setSubmitError("");
    submit.mutate(withClientRequestId(draftPayload, clientRequestId));
  };

  const generation = useQuery({
    queryKey: ["playground-generation", requestId],
    queryFn: () => api.playgroundGeneration(requestId),
    enabled: Boolean(requestId),
    retry: false,
    refetchIntervalInBackground: true,
    refetchInterval: (query) => {
      const status = query.state.data?.data.status;
      if (isTerminalStatus(status)) return false;
      if (query.state.error instanceof ApiError && query.state.error.status === 404) return false;
      return PLAYGROUND_POLL_INTERVAL_MS;
    },
  });
  const current = generation.data?.data;
  const terminal = isTerminalStatus(current?.status);
  useEffect(() => {
    if (terminal) void queryClient.invalidateQueries({ queryKey: ["playground-history"] });
  }, [queryClient, requestId, terminal]);

  const promptId = `${fieldId}-prompt`;
  const blockedId = `${fieldId}-blocked`;
  const uploadId = `${fieldId}-upload`;
  const modeRoles = form.mode.mode === "image_to_video" ? ["首帧", "尾帧"] : [];

  return <div className="playground-panel" hidden={hidden}>
    <section className="content-section playground-form" aria-labelledby={`${fieldId}-title`}>
      <div className="section-heading">
        <div><h2 id={`${fieldId}-title`}>{copy.title}</h2><p aria-live="polite">当前模式：{form.mode.allowed ? form.mode.hint : "不可提交"}</p></div>
      </div>
      <div className="playground-form-body">
        <label className="playground-field"><span>测试模式</span>
          <select value={testMode} onChange={(event) => { setTestMode(event.target.value as PlaygroundTestMode); setChannelId(0); }}>
            <option value="model">模型测试（禁止跨模型降级）</option>
            <option value="channel">指定渠道测试（禁止切换渠道或模型）</option>
            <option value="policy">业务策略测试（允许替换与降级）</option>
          </select>
        </label>
        {testMode === "policy" ? <p className="inline-warning">按测试 Consumer 的完整策略执行，实际模型和渠道可能与所选模型不同。</p> : <p className="muted">仅测试所选模型。{testMode === "model" ? "渠道自动选择，可在同模型的授权渠道间切换。" : "所选渠道失败后不会自动切换。"}</p>}
        <label className="playground-field"><span>模型</span>
          <select value={form.selection.model} disabled={!models.length} onChange={(event) => { setDraft((value) => ({ ...value, model: event.target.value })); setChannelId(0); }}>
            {models.length ? models.map((item) => <option key={item.key} value={item.key}>{item.key}</option>) : <option value="">暂无可用模型</option>}
          </select>
        </label>
        {testMode === "channel" ? <label className="playground-field"><span>测试渠道</span>
          <select value={channelId} onChange={(event) => setChannelId(Number(event.target.value))}>
            <option value={0}>请选择渠道</option>
            {channels.map((item) => <option key={item.channel_id} value={item.channel_id}>{item.display_name || `渠道 #${item.channel_id}`}{item.unavailable_reason ? ` · ${item.unavailable_reason}` : ""}</option>)}
          </select>
        </label> : testMode === "model" ? <p className="muted">测试渠道：自动（仅当前模型）</p> : null}
        <div className="playground-field">
          <label htmlFor={promptId}>提示词</label>
          <textarea id={promptId} rows={5} value={prompt} placeholder={copy.placeholder} aria-describedby={`${promptId}-hint`}
            onChange={(event) => setPrompt(event.target.value)}
            onKeyDown={(event) => { if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) { event.preventDefault(); startSubmit(); } }} />
          <small id={`${promptId}-hint`}>Ctrl / ⌘ + Enter 快速提交</small>
        </div>
        {form.aspectRatios.length ? <OptionGroup legend="画面比例" value={form.selection.aspectRatio} onChange={(aspectRatio) => setDraft((value) => ({ ...value, aspectRatio }))}
          options={form.aspectRatios.map((item) => ({ value: item, label: aspectRatioLabel(item) }))} /> : null}
        {form.resolutions.length > 1 ? <OptionGroup legend={copy.resolution} value={form.selection.resolution} onChange={(resolution) => setDraft((value) => ({ ...value, resolution }))}
          options={form.resolutions.map((item) => ({ value: item, label: resolutionLabel(item) }))} /> : null}
        {modality === "video" && form.durations.length ? <label className="playground-field"><span>时长</span>
          <select value={form.selection.durationSeconds} onChange={(event) => setDraft((value) => ({ ...value, durationSeconds: Number(event.target.value) }))}>
            {form.durations.map((item) => <option key={item} value={item}>{durationLabel(item)}</option>)}
          </select>
        </label> : null}
        <div className="playground-field">
          <span id={`${uploadId}-label`}>参考图 <small>{uploads.length} / {form.uploadLimit} 张</small></span>
          {uploads.length ? <ul className="playground-upload-grid" aria-labelledby={`${uploadId}-label`}>
            {uploads.map((item, index) => <li key={item.id} className={`playground-upload is-${item.status}`}>
              {item.previewUrl ? <img src={item.previewUrl} alt={`参考图 ${index + 1}：${item.file.name}`} /> : <span className="playground-upload-name">{item.file.name}</span>}
              {modeRoles[index] ? <span className="playground-upload-role">{modeRoles[index]}</span> : null}
              {item.status === "uploading" ? <span className="playground-upload-state" role="status"><RefreshCw size={16} className="spin" />上传中</span> : null}
              {item.status === "error" ? <button type="button" className="playground-upload-retry" onClick={() => retryUpload(item)} aria-label={`重试上传 ${item.file.name}`}><RotateCcw size={16} />重试</button> : null}
              <button type="button" className="icon-button playground-upload-remove" onClick={() => removeUpload(item.id)} aria-label={`删除参考图 ${index + 1}`}><X size={14} /></button>
            </li>)}
          </ul> : null}
          {uploads.filter((item) => item.status === "error").map((item) => <span key={item.id} className="field-error-text" role="alert">{item.file.name}：{item.error}</span>)}
          <label className={`secondary-button playground-upload-picker ${uploads.length >= form.uploadLimit ? "is-disabled" : ""}`}>
            <ImagePlus size={16} />选择本地图片
            <input className="visually-hidden" type="file" multiple accept={PLAYGROUND_UPLOAD_TYPES.join(",")}
              disabled={uploads.length >= form.uploadLimit} aria-describedby={`${uploadId}-help`}
              onChange={(event) => { const files = Array.from(event.target.files || []); event.target.value = ""; addFiles(files); }} />
          </label>
          <small id={`${uploadId}-help`}>{form.uploadLimit ? `${copy.uploadHelp}PNG / JPEG / WebP，单张不超过 10MB。` : "当前模型不支持参考图。"}</small>
          {uploadNotice ? <span className="field-error-text" role="alert">{uploadNotice}</span> : null}
        </div>
      </div>
      <div className="playground-submit">
        <button type="button" className="primary-button" disabled={!canSubmit} aria-describedby={disabledReason ? blockedId : undefined} onClick={startSubmit}>
          {submit.isPending ? <RefreshCw size={16} className="spin" /> : <Sparkles size={16} />}{submit.isPending ? "提交中" : copy.submit}
        </button>
        {disabledReason ? <span id={blockedId} className="playground-blocked" aria-live="polite">{disabledReason}</span> : null}
      </div>
      {submitError ? <div className="inline-error playground-submit-error" role="alert">
        <span>提交失败：{submitError}</span>
        {retrySameId ? <>
          <span>请求可能已到达网关。原样重试会复用同一个请求 ID，不会重复生成。</span>
          <button type="button" className="secondary-button compact" disabled={submit.isPending} onClick={startSubmit}><RotateCcw size={14} />使用同一请求 ID 重试</button>
        </> : null}
      </div> : null}
    </section>
    <PlaygroundResult modality={modality} generation={current} deduplicated={deduplicated} pollError={generation.error} onPreview={onPreview} />
  </div>;
}

// PlaygroundResult shows the latest request's status, media and execution facts.
// 展示最近一次请求的状态、产物和执行信息。
function PlaygroundResult({ modality, generation, deduplicated, pollError, onPreview }: {
  modality: Modality;
  generation?: PlaygroundGeneration;
  deduplicated: boolean;
  pollError: Error | null;
  onPreview: (request: LightboxRequest) => void;
}) {
  const { can } = useGatewayPermissions();
  const headingId = useId();
  const view = generation ? parseGenerationStatus(generation.status, generation.error_message) : null;
  const media = generationMedia(generation?.result);
  const notFound = pollError instanceof ApiError && pollError.status === 404;
  return <section className="content-section playground-result" aria-labelledby={headingId}>
    <div className="section-heading">
      <div><h2 id={headingId}>生成结果</h2><p>{generation ? `Request ID ${generation.request_id}` : "提交后每 3 秒刷新一次状态"}</p></div>
      {generation ? <StatusBadge value={view!.status} /> : null}
    </div>
    {!generation || !view ? <div className="section-empty">还没有提交{modality === "image" ? "图片" : "视频"}生成</div> : <div className="playground-result-body">
      <p className={`playground-status-line is-${view.tone}`} role="status">
        {!view.terminal ? <RefreshCw size={15} className="spin" /> : view.tone === "warning" || view.tone === "error" ? <AlertTriangle size={15} /> : null}
        {generation.waiting_capacity ? "允许的测试渠道容量不足，等待中；超时后结束，不会切换到约束外的模型或渠道" : view.message}
      </p>
      {generation.status === "uncertain" ? <div className="inline-warning" role="alert"><AlertTriangle size={16} /><span>上游结果无法确认，页面已停止轮询。请勿重复提交，联系网关管理员在请求排障中核对。</span></div> : null}
      {generation.status === "failed" && (generation.error_kind || generation.error_code) ? <p className="cell-note error-note">{[generation.error_kind, generation.error_code].filter(Boolean).join(" / ")}</p> : null}
      {deduplicated ? <div className="inline-note">本次提交命中了已有请求，没有重复生成。</div> : null}
      {pollError && !view.terminal ? <div className="inline-warning" role="alert"><AlertTriangle size={16} /><span>{notFound ? "找不到该请求，已停止刷新。" : `状态刷新失败：${pollError.message}，稍后自动重试。`}</span></div> : null}
      {view.succeeded ? media.length
        ? <MediaGrid media={media} title="生成结果" onPreview={onPreview} />
        : <div className="section-empty">请求已完成，但结果中没有可预览的地址</div> : null}
      <p className="playground-test-target" aria-label="测试目标"><span>{playgroundRoutingLabel(generation.playground)}</span><br /><span>指定模型：{generation.requested_model || "-"}</span></p>
      <dl className="playground-facts">
        <div><dt>实际模型</dt><dd>{generation.executed_model || "尚未执行"}</dd></div>
        <div><dt>渠道</dt><dd>{generation.executed_channel || "尚未选定"}</dd></div>
        <div><dt>耗时</dt><dd>{formatElapsed(elapsedSeconds(generation.created_at, generation.finished_at, Math.floor(Date.now() / 1000)))}</dd></div>
        <div><dt>成本</dt><dd>{formatMoney(generation.total_cost, generation.currency)}</dd></div>
      </dl>
      {generation.attempts_unavailable ? <p role="alert">尝试记录读取失败，请稍后刷新。</p> : null}
      {generation.attempts?.length ? <ol aria-label="渠道尝试记录">{generation.attempts.map((item) => <li key={item.attempt_no}>第 {item.attempt_no} 次：{item.model || "未知模型"} / {item.channel} · {item.status}{item.error_kind || item.error_code ? ` · ${[item.error_kind, item.error_code].filter(Boolean).join(" / ")}` : ""}</li>)}</ol> : null}
      {can("requests.read") ? <Link className="row-link" to={`/manage/requests/${encodeURIComponent(generation.request_id)}`}>查看请求详情 <ArrowUpRight size={15} /></Link> : null}
    </div>}
  </section>;
}

// MediaGrid renders keyboard-reachable thumbnails that open the shared lightbox.
// 渲染可键盘操作的缩略图，点击后打开灯箱。
export function MediaGrid({ media, title, onPreview }: { media: PlaygroundMedia[]; title: string; onPreview: (request: LightboxRequest) => void }) {
  return <ul className="playground-media-grid">
    {media.map((item, index) => {
      const label = `预览${item.type === "video" ? "视频" : "图片"} ${index + 1}`;
      return <li key={`${index}-${item.url}`}>
        <button type="button" className="playground-media-button" aria-label={label} onClick={() => onPreview({ items: media, startIndex: index, title })}>
          {item.type === "video" ? <><video src={item.url} muted preload="metadata" aria-hidden="true" /><span className="playground-play"><Play size={18} /></span></> : <img src={item.url} alt="" loading="lazy" />}
        </button>
      </li>;
    })}
  </ul>;
}

function OptionGroup({ legend, value, options, onChange }: { legend: string; value: string; options: Array<{ value: string; label: string }>; onChange: (value: string) => void }) {
  return <fieldset className="playground-field playground-options">
    <legend>{legend}</legend>
    <div className="segmented-control">
      {options.map((option) => <button type="button" key={option.value} aria-pressed={option.value === value} className={option.value === value ? "active" : ""} onClick={() => onChange(option.value)}>{option.label}</button>)}
    </div>
  </fieldset>;
}

// errorMessage keeps backend messages and maps transport failures (fetch TypeError) to one localized hint.
// 保留后端错误信息；网络层失败（fetch TypeError）统一显示中文提示。
function errorMessage(error: unknown): string {
  if (error instanceof ApiError && error.message) return error.message;
  return "网络异常，请检查连接后重试";
}

function createPreview(file: File): string {
  return typeof URL.createObjectURL === "function" ? URL.createObjectURL(file) : "";
}

function revokePreview(url: string) {
  if (url && typeof URL.revokeObjectURL === "function") URL.revokeObjectURL(url);
}
